import { Vector2 } from 'three';

/** Target multiplier over CSS canvas size (3× ≈ print-friendly on typical displays). */
export const DEFAULT_EXPORT_SCALE = 3;
/** Avoid exhausting GPU memory on very large viewports. */
export const MAX_EXPORT_DIMENSION = 8192;

function resolveExportScale(cssW, cssH, requestedScale) {
  const maxDim = Math.max(cssW, cssH);
  if (maxDim <= 0) return requestedScale;
  if (maxDim * requestedScale <= MAX_EXPORT_DIMENSION) return requestedScale;
  return MAX_EXPORT_DIMENSION / maxDim;
}

/**
 * Re-render the scene at higher pixel density, copy to a 2D canvas, then restore the renderer.
 */
export function captureHighResFrame(gl, scene, camera, { scale = DEFAULT_EXPORT_SCALE } = {}) {
  if (!gl?.domElement || !scene || !camera) {
    throw new Error('Scene is not ready');
  }

  const dom = gl.domElement;
  const cssW = dom.clientWidth;
  const cssH = dom.clientHeight;
  if (!cssW || !cssH) throw new Error('Empty canvas');

  const effectiveScale = resolveExportScale(cssW, cssH, scale);
  const prevPixelRatio = gl.getPixelRatio();
  const prevSize = new Vector2();
  gl.getSize(prevSize);

  try {
    gl.setPixelRatio(effectiveScale);
    gl.setSize(cssW, cssH, false);
    gl.render(scene, camera);

    const w = dom.width;
    const h = dom.height;
    const out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    const ctx = out.getContext('2d');
    if (!ctx) throw new Error('Could not create export canvas');
    ctx.drawImage(dom, 0, 0, w, h);

    return { canvas: out, width: w, height: h, cssWidth: cssW, cssHeight: cssH, scale: effectiveScale };
  } finally {
    gl.setPixelRatio(prevPixelRatio);
    gl.setSize(prevSize.x, prevSize.y);
    gl.render(scene, camera);
  }
}
